/**
 * Geometry for framing a product photo inside the mobile product card
 * (issue #142).
 *
 * On a phone the product list shows one card per row, so the photo area is the
 * full card width but only 200px tall, and the storefront crops it with
 * `object-fit: cover`. A photo framed for the narrower desktop card loses its
 * top and bottom. These helpers let an admin pan and zoom the photo inside that
 * frame and turn what they see into the mobile photo.
 */

/** The card on a typical 375px-wide phone: the page leaves a 16px gutter each side. */
export const MOBILE_CARD_WIDTH = 343;
/** Fixed photo height of the product card (Products.tsx). */
export const MOBILE_CARD_IMAGE_HEIGHT = 200;
/** Saved at 3x so the photo stays sharp on high-density phone screens. */
export const MOBILE_PHOTO_OUTPUT_SCALE = 3;

export interface Size {
  width: number;
  height: number;
}

export interface Offset {
  x: number;
  y: number;
}

/** The smallest scale at which the photo still covers the whole frame. */
export const coverScale = (image: Size, frame: Size): number =>
  Math.max(frame.width / image.width, frame.height / image.height);

/**
 * The largest scale at which the whole photo fits inside the frame. Zooming
 * out to here shows the entire photo, with white filling the rest of the card.
 */
export const containScale = (image: Size, frame: Size): number =>
  Math.min(frame.width / image.width, frame.height / image.height);

/**
 * Keep the photo where it makes sense. Along a side where the photo is larger
 * than the frame, it must keep covering the frame (no gap). Along a side where
 * it is smaller (zoomed out to show the whole photo), it must stay inside it.
 */
export const clampOffset = (offset: Offset, image: Size, scale: number, frame: Size): Offset => {
  const spareX = frame.width - image.width * scale;
  const spareY = frame.height - image.height * scale;
  return {
    x: Math.min(Math.max(0, spareX), Math.max(Math.min(0, spareX), offset.x)),
    y: Math.min(Math.max(0, spareY), Math.max(Math.min(0, spareY), offset.y)),
  };
};

/** The offset that centres the photo in the frame at the given scale. */
export const centredOffset = (image: Size, scale: number, frame: Size): Offset => ({
  x: (frame.width - image.width * scale) / 2,
  y: (frame.height - image.height * scale) / 2,
});

/**
 * Change the scale while keeping the point at the centre of the frame where it
 * is, so zooming feels anchored rather than drifting to a corner.
 */
export const zoomAboutCentre = (
  offset: Offset,
  fromScale: number,
  toScale: number,
  image: Size,
  frame: Size
): Offset => {
  const centreX = (frame.width / 2 - offset.x) / fromScale;
  const centreY = (frame.height / 2 - offset.y) / fromScale;
  return clampOffset(
    { x: frame.width / 2 - centreX * toScale, y: frame.height / 2 - centreY * toScale },
    image,
    toScale,
    frame
  );
};

/**
 * Where to draw the photo on an output canvas that is the frame enlarged by
 * `outputScale`. Drawing the whole photo and letting the canvas clip it works
 * the same whether the photo overflows the frame or sits inside it.
 */
export const destRect = (offset: Offset, image: Size, scale: number, outputScale: number) => ({
  dx: offset.x * outputScale,
  dy: offset.y * outputScale,
  dw: image.width * scale * outputScale,
  dh: image.height * scale * outputScale,
});
